// apps/web/src/features/admin/automations/list/AutomationList.tsx
import { useMemo, useState } from 'react';
import {
  assembleAutomations, automationKey, describeAutomation, compileAutomation, newAutomationGroupId,
  stepsForStage, STEP_LABELS, MOMENT_PHRASE, walkConditionGroup,
} from '@capture/shared';
import type {
  Automation, AutomationBranch, RuleAttachment, CreateRuleAttachmentInput,
  FieldContent, Behavior, Stage,
} from '@capture/shared';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { Collapsible } from '@/components/Collapsible';
import { identifyFillTransformStep, summarizeEffectStep } from '../identify-effect';
import { identifyRuleStep, summarizeRuleStep } from '../rule-identify';
import styles from './AutomationList.module.css';

/** Minimal per-form shape the list needs — a subset of FormContent so callers
 *  don't have to hand in the whole draft. */
export interface AutomationListForm {
  id: string;
  name: string;
  fields: FieldContent[];
  rules: RuleAttachment[];
}

type MomentGroupId = 'working' | 'entering_step' | 'export' | 'batch';
const GROUP_ORDER: MomentGroupId[] = ['working', 'entering_step', 'export', 'batch'];
const GROUP_LABELS: Record<MomentGroupId, string> = {
  working: 'As operators work',
  entering_step: 'Entering a step',
  export: 'At export',
  batch: 'Batch events',
};
const ORPHAN_GROUP = 'orphans';
const ORPHAN_LABEL = 'Unrecognized rules';

function momentGroup(a: Automation): MomentGroupId {
  switch (a.moment) {
    case 'keying':
    case 'on_validate':
      return 'working';
    case 'step_enter':
      return 'entering_step';
    case 'pre_export':
      return 'export';
    default:
      return 'batch';
  }
}

const OUTCOME_FAMILY: Record<string, string> = {
  route_to_queue: 'route', skip_step: 'route', reclassify: 'route',
  hold: 'hold', escalate: 'escalate', notify: 'notify', reject: 'block',
};
const ALL_OUTCOME_FAMILIES = ['warn', 'block', 'route', 'hold', 'escalate', 'notify', 'fill'] as const;

function branchFamilies(b: AutomationBranch): string[] {
  if (b.family === 'check') return [b.severity === 'error' ? 'block' : 'warn'];
  return b.actions.map((a) => OUTCOME_FAMILY[a.effectKey] ?? a.effectKey);
}

/** Field names referenced by a branch's condition — reuses the shared
 *  condition-tree walker (`walkConditionGroup`) rather than re-implementing
 *  the recursion here. */
function conditionFieldNames(g: AutomationBranch['condition'], out: Set<string>): void {
  if (!g) return;
  walkConditionGroup(g, [], (single) => {
    if (single.source.type === 'field') out.add(single.source.name);
    if (single.value?.type === 'field') out.add(single.value.name);
  });
}

/** Per-automation derived info collected in a single pass over its branches:
 *  the member rule ids (for group toggle/delete), every field name touched
 *  (scope field + condition refs, for the field filter), and the outcome
 *  families (for the outcome chips/filter). */
function branchInfo(a: Automation): { ruleIds: string[]; fieldNames: string[]; outcomeFamilies: string[] } {
  const ruleIds: string[] = [];
  const fieldNames = new Set<string>(a.fieldName ? [a.fieldName] : []);
  const outcomeFamilies = new Set<string>();
  for (const b of a.branches) {
    if (b.ruleId) ruleIds.push(b.ruleId);
    conditionFieldNames(b.condition, fieldNames);
    for (const family of branchFamilies(b)) outcomeFamilies.add(family);
  }
  return { ruleIds, fieldNames: [...fieldNames], outcomeFamilies: [...outcomeFamilies] };
}

/** Steps chip: the job's own pipeline-step labels when available (an author
 *  may have renamed "Verify" to something else), falling back to the
 *  canonical `STEP_LABELS`; unstaged automations fall back to the shared
 *  moment phrase (`MOMENT_PHRASE`) so this chip and the generated sentence's
 *  "when" lead-in never drift out of sync. */
function stepsChipFor(a: Automation, pipelineSteps: { stepType: string; label: string }[]): string {
  if (a.stages?.length) {
    const labels = a.stages.flatMap((s: Stage) =>
      stepsForStage(s).map((st) => pipelineSteps.find((p) => p.stepType === st)?.label ?? STEP_LABELS[st]),
    );
    return [...new Set(labels)].join(' / ');
  }
  return MOMENT_PHRASE[a.moment];
}

function scopeChipFor(a: Automation, fieldLabel: string): string {
  if (a.scope === 'field') return fieldLabel || a.fieldName || 'Field';
  if (a.scope === 'batch') return 'Batch';
  return 'Form';
}

interface AutomationRow {
  kind: 'automation';
  key: string;
  formId: string; formName: string;
  automation: Automation;
  ruleIds: string[];
  name: string;
  sentence: string;
  group: MomentGroupId;
  fieldNames: string[];
  scopeChip: string;
  stepsChip: string;
  outcomeFamilies: string[];
  active: boolean;
}
interface BehaviorRow {
  kind: 'behavior';
  key: string;
  formId: string; formName: string;
  fieldName: string; fieldLabel: string; stage: Stage;
  behaviorIndex: number; effectKey: string; label: string; summary: string;
  behavior: Behavior;
}
interface OrphanRow {
  kind: 'orphan';
  key: string;
  formId: string; formName: string;
  rule: RuleAttachment;
  label: string;
  summary: string;
}
type ListRow = AutomationRow | BehaviorRow | OrphanRow;

function collectBehaviorRows(forms: AutomationListForm[]): BehaviorRow[] {
  const out: BehaviorRow[] = [];
  for (const form of forms) {
    for (const field of form.fields ?? []) {
      const stageMap = (field.stageMap ?? {}) as Record<string, unknown[]>;
      for (const [stage, behaviors] of Object.entries(stageMap)) {
        (behaviors ?? []).forEach((behavior, index) => {
          const id = identifyFillTransformStep(behavior);
          if (!id) return;
          out.push({
            kind: 'behavior',
            key: `${form.id}-${field.name}-${stage}-${index}`,
            formId: form.id, formName: form.name,
            fieldName: field.name, fieldLabel: field.label || field.name,
            stage: stage as Stage, behaviorIndex: index,
            effectKey: id.effectKey, label: id.label,
            summary: summarizeEffectStep(id.effectKey, id.step.config),
            behavior: behavior as Behavior,
          });
        });
      }
    }
  }
  return out;
}

function buildRows(forms: AutomationListForm[], pipelineSteps: { stepType: string; label: string }[]): ListRow[] {
  const rows: ListRow[] = [];
  for (const form of forms) {
    // Built once per form so `scopeChipFor` doesn't linear-scan `form.fields`
    // for every automation that scopes to a field.
    const fieldLabelByName = new Map(form.fields.map((f) => [f.name, f.label || f.name]));
    const { automations, orphans } = assembleAutomations(form.rules ?? []);
    for (const a of automations) {
      const { ruleIds, fieldNames, outcomeFamilies } = branchInfo(a);
      rows.push({
        kind: 'automation',
        // F-065 — shared with the API's automation guide; one definition of
        // 'an automation's stable id' rather than a copy per listing site.
        key: automationKey(a),
        formId: form.id, formName: form.name,
        automation: a, ruleIds,
        name: a.name,
        sentence: describeAutomation(a),
        group: momentGroup(a),
        fieldNames,
        scopeChip: scopeChipFor(a, (a.fieldName && fieldLabelByName.get(a.fieldName)) || a.fieldName || ''),
        stepsChip: stepsChipFor(a, pipelineSteps),
        outcomeFamilies,
        active: a.isActive,
      });
    }
    for (const rule of orphans) {
      // Best-effort label only — assembleAutomations already decided this
      // rule (or a member of its group) doesn't parse, so this is a hint for
      // the operator, not a claim that the rule is understood.
      const identified = identifyRuleStep(rule);
      rows.push({
        kind: 'orphan',
        key: `orphan-${form.id}-${rule.id}`,
        formId: form.id, formName: form.name,
        rule,
        label: identified?.label ?? rule.type ?? 'Unrecognized rule',
        summary: identified ? summarizeRuleStep(identified.effectKey, identified.step.config) || identified.step.moment : (rule.name || rule.id),
      });
    }
  }
  rows.push(...collectBehaviorRows(forms));
  return rows;
}

export interface AutomationListProps {
  forms: AutomationListForm[];
  pipelineSteps?: { stepType: string; label: string }[];
  onEdit: (automation: Automation, formId: string) => void;
  onEditBehavior?: (row: {
    formId: string; fieldName: string; stage: Stage; behaviorIndex: number; behavior: Behavior; effectKey: string;
  }) => void;
  onToggleActive: (formId: string, ruleIds: string[], nextActive: boolean) => void;
  onDuplicate: (formId: string, inputs: CreateRuleAttachmentInput[]) => void;
  onDelete: (formId: string, ruleIds: string[]) => void;
}

export function AutomationList({
  forms, pipelineSteps = [], onEdit, onEditBehavior, onToggleActive, onDuplicate, onDelete,
}: AutomationListProps) {
  const rows = useMemo(() => buildRows(forms, pipelineSteps), [forms, pipelineSteps]);

  const [search, setSearch] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [formFilter, setFormFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<'' | 'active' | 'inactive'>('');
  const [pendingDelete, setPendingDelete] = useState<{ formId: string; ruleIds: string[]; label: string } | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const allFieldNames = useMemo(() => {
    const out = new Set<string>();
    for (const r of rows) {
      if (r.kind === 'automation') for (const f of r.fieldNames) out.add(f);
      if (r.kind === 'behavior') out.add(r.fieldName);
    }
    return [...out].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (formFilter && r.formId !== formFilter) return false;
      if (fieldFilter) {
        const touches =
          r.kind === 'automation' ? r.fieldNames.includes(fieldFilter)
          : r.kind === 'behavior' ? r.fieldName === fieldFilter
          : false;
        if (!touches) return false;
      }
      if (outcomeFilter) {
        const families = r.kind === 'automation' ? r.outcomeFamilies : r.kind === 'behavior' ? ['fill'] : [];
        if (!families.includes(outcomeFilter)) return false;
      }
      if (activeFilter && r.kind === 'automation') {
        if (activeFilter === 'active' && !r.active) return false;
        if (activeFilter === 'inactive' && r.active) return false;
      }
      if (q) {
        const haystack =
          r.kind === 'automation' ? `${r.name} ${r.sentence}`
          : r.kind === 'behavior' ? `${r.label} ${r.fieldLabel} ${r.summary}`
          : `${r.label} ${r.summary}`;
        if (!haystack.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, fieldFilter, formFilter, outcomeFilter, activeFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, ListRow[]>();
    for (const id of GROUP_ORDER) map.set(id, []);
    map.set(ORPHAN_GROUP, []);
    for (const r of filtered) {
      const gid = r.kind === 'orphan' ? ORPHAN_GROUP : r.kind === 'behavior' ? 'working' : r.group;
      map.get(gid)!.push(r);
    }
    return map;
  }, [filtered]);

  function toggleCollapsed(gid: string) {
    setCollapsed((prev) => ({ ...prev, [gid]: !prev[gid] }));
  }

  function handleDuplicate(row: AutomationRow) {
    const a = row.automation;
    const isMulti = a.branches.length > 1;
    const copy: Automation = {
      ...a,
      name: `${a.name} (copy)`,
      groupId: isMulti ? newAutomationGroupId() : null,
      branches: a.branches.map((b) => ({ ...b, ruleId: undefined })),
    };
    const inputs = compileAutomation(copy);
    onDuplicate(row.formId, inputs);
  }

  function confirmDelete() {
    if (!pendingDelete) return;
    onDelete(pendingDelete.formId, pendingDelete.ruleIds);
    setPendingDelete(null);
  }

  const groupIds = [...GROUP_ORDER, ORPHAN_GROUP].filter((gid) => (grouped.get(gid) ?? []).length > 0);

  return (
    <div className={styles.list} data-testid="automation-list">
      <div className={styles.filters}>
        <input
          type="search"
          data-testid="automation-list-search"
          placeholder="Search automations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select data-testid="automation-list-filter-field" value={fieldFilter} onChange={(e) => setFieldFilter(e.target.value)}>
          <option value="">All fields</option>
          {allFieldNames.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select data-testid="automation-list-filter-form" value={formFilter} onChange={(e) => setFormFilter(e.target.value)}>
          <option value="">All forms</option>
          {forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <select data-testid="automation-list-filter-outcome" value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)}>
          <option value="">All outcomes</option>
          {ALL_OUTCOME_FAMILIES.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select
          data-testid="automation-list-filter-active"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as '' | 'active' | 'inactive')}
        >
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {groupIds.length === 0 && (
        <div className={styles.empty} data-testid="automation-list-empty">
          {rows.length === 0 ? 'No automations yet.' : 'No automations match your filters.'}
        </div>
      )}

      {groupIds.map((gid) => {
        const groupRows = grouped.get(gid) ?? [];
        const isOpen = !collapsed[gid];
        const label = gid === ORPHAN_GROUP ? ORPHAN_LABEL : GROUP_LABELS[gid as MomentGroupId];
        return (
          <Collapsible
            key={gid}
            open={isOpen}
            onToggle={() => toggleCollapsed(gid)}
            className={styles.group}
            headerClassName={styles.groupHeader}
            bodyClassName={styles.groupBody}
            chevronClassName={styles.chevron}
            testId={`automation-list-group-${gid}`}
            headerTestId={`automation-list-group-toggle-${gid}`}
            header={
              <>
                <span className={styles.groupTitle}>{label}</span>
                <span className={styles.groupCount}>{groupRows.length}</span>
              </>
            }
          >
                {groupRows.map((r) => {
                  if (r.kind === 'behavior') {
                    return (
                      <div
                        key={r.key}
                        role="button" tabIndex={0}
                        className={`${styles.row} ${styles.rowClickable}`}
                        data-testid={`automation-list-row-${r.key}`}
                        onClick={() => onEditBehavior?.(r)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEditBehavior?.(r); } }}
                      >
                        <div className={styles.rowMain}>
                          <span className={styles.rowName}>{r.label}</span>
                          <span className={styles.rowSentence}>{r.summary}</span>
                        </div>
                        <div className={styles.chips}>
                          <span className={styles.chip}>{r.formName}</span>
                          <span className={styles.chip}>{r.fieldLabel}</span>
                          <span className={styles.chip}>{r.stage}</span>
                        </div>
                      </div>
                    );
                  }
                  if (r.kind === 'orphan') {
                    return (
                      <div key={r.key} className={styles.row} data-testid={`automation-list-row-${r.key}`}>
                        <div className={styles.rowMain}>
                          <span className={styles.rowName}>{r.rule.name || r.label}</span>
                          <span className={styles.rowSentence}>{r.label}</span>
                        </div>
                        <div className={styles.chips}>
                          <span className={styles.chip}>{r.formName}</span>
                        </div>
                        <div className={styles.actions}>
                          <button
                            type="button" className="btn btn--danger"
                            data-testid={`automation-list-delete-${r.key}`}
                            onClick={() => setPendingDelete({ formId: r.formId, ruleIds: [r.rule.id], label: r.rule.name || r.label })}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={r.key} className={styles.row} data-testid={`automation-list-row-${r.key}`}>
                      <div
                        className={`${styles.rowMain} ${styles.rowClickable}`}
                        role="button" tabIndex={0}
                        data-testid={`automation-list-edit-${r.key}`}
                        onClick={() => onEdit(r.automation, r.formId)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(r.automation, r.formId); } }}
                      >
                        <span className={styles.rowName}>{r.name}</span>
                        <span className={styles.rowSentence}>{r.sentence}</span>
                      </div>
                      <div className={styles.chips}>
                        <span className={styles.chip}>{r.formName}</span>
                        <span className={styles.chip}>{r.scopeChip}</span>
                        <span className={styles.chip}>{r.stepsChip}</span>
                        {r.outcomeFamilies.map((o) => <span className={styles.chip} key={o}>{o}</span>)}
                      </div>
                      <div className={styles.actions}>
                        <label className={styles.toggle}>
                          <input
                            type="checkbox"
                            role="switch"
                            checked={r.active}
                            data-testid={`automation-list-toggle-${r.key}`}
                            onChange={(e) => {
                              e.stopPropagation();
                              onToggleActive(r.formId, r.ruleIds, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </label>
                        <button
                          type="button" className="btn"
                          data-testid={`automation-list-duplicate-${r.key}`}
                          onClick={(e) => { e.stopPropagation(); handleDuplicate(r); }}
                        >
                          Duplicate
                        </button>
                        <button
                          type="button" className="btn btn--danger"
                          data-testid={`automation-list-delete-${r.key}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDelete({ formId: r.formId, ruleIds: r.ruleIds, label: r.name });
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
          </Collapsible>
        );
      })}

      {pendingDelete && (
        <ConfirmDeleteDialog
          title="Delete automation"
          message={`Delete "${pendingDelete.label}"? This cannot be undone.`}
          busy={false}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
          showRescanOption={false}
        />
      )}
    </div>
  );
}
